<template>
    <div
        :class="classFilterAccordion"
        class="filter-accordion"
    >
        <div
            ref="titleEl"
            class="title"
            tabindex="0"
            @click.prevent.stop="toggle"
            @keypress.enter.prevent.stop="toggle"
        >
            {{ title }}

            <span
                v-if="count"
                class="count"
            >
                &nbsp;{{ hideCount ? '•' : `(${count})` }}
            </span>

            <Icon
                class="icon"
                icon="angle-down"
            />
        </div>

        <div class="content">
            <div
                ref="wrapper"
                class="cw"
            >
                <slot :disabled="!isOpen"></slot>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, type Ref } from 'vue'
import { useElementSize } from '@vueuse/core'

import Icon from '../icon/Icon.vue'

const {
    count,
    openAtStart = false,
    multiple = false,
} = defineProps<{
    title: string
    count: number | undefined
    hideCount?: boolean
    openAtStart?: boolean
    multiple?: boolean
}>()

defineSlots<{
    default(props: { disabled: boolean }): any
}>()

const titleEl: Ref<HTMLElement | null> = ref(null)
defineExpose({ titleEl })

const isOpen = ref(openAtStart)
const toggle = () => {
    isOpen.value = !isOpen.value
}

const wrapper = ref<HTMLElement | null>(null)
const { height } = useElementSize(wrapper, undefined, { box: 'border-box' })
const heightProperty = ref(`${height.value}px`)
watch(
    height,
    (h) => {
        // avoids excessive animation on modal open
        if (h) {
            heightProperty.value = `${h}px`
        }
    },
    { immediate: true }
)

watch(
    () => count,
    (c) => {
        if (c) {
            isOpen.value = true
        }
    },
    { immediate: true }
)

const classFilterAccordion = computed(() => ({
    'open': isOpen.value,
}))

const filterMarginBottom = computed(() => (multiple ? '20px' : '0'))
</script>

<style lang="scss" scoped>
.filter-accordion {
    --time-open: 0.2s;
    --time-close: 0.12s;
    border-bottom: 1px solid var(--grey-200);
    position: relative;

    .title {
        height: 60px;
        display: flex;
        align-items: center;
        padding: 0 10px;
        user-select: none;
        cursor: pointer;

        font-family: $effra-font-stack;
        font-size: 14px;
        text-transform: uppercase;

        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.05em;
        text-align: center;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;

        .icon {
            margin-left: auto;
            display: block;
            transition: transform var(--time-close)
                var(--timing-function-easing-emphasized);
        }
    }

    .content {
        height: 0;
        padding: 0 2px;
        opacity: 0;
        transition: all var(--time-close)
            var(--timing-function-easing-emphasized);
        overflow: hidden;

        .cw {
            padding: 10px 0 20px 0;
        }

        ::v-deep(.filter-base) {
            margin-bottom: v-bind(filterMarginBottom);
        }
    }

    &.open {
        .title .icon {
            transform: rotateZ(180deg);
            transition: transform var(--time-open)
                var(--timing-function-easing-emphasized);
        }

        .content {
            height: v-bind(heightProperty);
            opacity: 1;
            transition: all var(--time-open)
                var(--timing-function-easing-emphasized-reverse);
        }
    }
}
</style>
